import { FC, useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';

import { Empty } from 'antd';
import moment from 'moment';
import { useLocation } from 'react-router-dom';

import BubbleChartType from './components/bubble-chart/BubbleChart';
import ButtonsType from './components/ButtonsType';
import { useDebounce } from '../../../hooks/useDebounce';
import {
    convertYToNumber,
    formatPeriodDate,
    getPlanSeries,
    isNumeric
} from '../../../../../helpers/utils/functions';
import {
    appendDetails,
    buildSeriesForBubbleChart,
    calculateMaxMinBubbleChart,
    checkMetricsGroups,
    findRowRecurse,
    getGroupDataForBarChart,
    predictDotsCount,
    buildGroupingsNames,
    resolveGroupAxisFilter,
    groupSuffixByReportType,
} from '../../../../../helpers/utils/reports';
import { unApi } from '../../../api/endpoints/reports/unApi';
import { useActions } from '../../../hooks/useActions';
import { useTitles } from '../../../hooks/useTitles';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import { useDashboardBlockSettings } from '../../../hooks/useDashboardBlockSettings';
import EChart from '../EChart';
import { getColorTitle } from '../cohort/components/utils';
import { useModalState } from '../dashboards/ContextModal';
import { TitleChart } from '../title-chart/TitleChart';
import { getMetricDataKey, hasMetricByDataKey } from '../metrics/utils';
import {
    getLineChartOption,
    getAreaChartOption,
    getPieChartOption,
    getBarChartOption,
    getFunnelChartOption,
    getGaugeChartOption,
    getTreemapChartOption,
    cleanHtmlTags,
    CHART_COLORS,
    lightenColor,
    MetricDisplaySettings,
} from '../charts/EChartConfig';
import { isCompareMetricKey, toCompareMetricKey, toLegacyCompareMetricKey } from '../../../constants/compare';
import { validateChartRequest } from '../../../../../helpers/utils/reportValidation';
import { isDeletedMetricsError, handleDeletedMetricsError } from '../../../../../helpers/utils/deletedMetrics';
import { isDeletedGroupsError, handleDeletedGroupsError } from '../../../../../helpers/utils/deletedGroups';
import { mergeAndBuildApiFilters } from '../params/filter/apiFilters';

// ==================== УТИЛИТЫ ====================

/**
 * Извлекает ID из name, если он содержится в скобках в конце.
 * Например: "fabricflowers (120232232941490340)" -> "120232232941490340"
 */
const extractIdFromName = (name: string): string | null => {
    if (!name || typeof name !== 'string') return null;
    
    // Ищем число в скобках в конце строки
    const match = name.match(/\((\d+)\)\s*$/);
    if (match && match[1]) {
        return match[1];
    }
    
    return null;
};

/**
 * Нормализует элемент фильтра, исправляя ID.
 * Если ID невалидный (например, "[object Object]"), пытается извлечь его из name.
 */
const normalizeFilterItem = (item: any): any => {
    if (!item) return item;
    
    let id = item.id;
    
    // Проверяем, является ли ID невалидным
    const strId = String(id);
    const isInvalidId = strId === '[object Object]' || strId.trim() === '';
    
    if (isInvalidId && item.name) {
        // Пытаемся извлечь ID из name
        const extractedId = extractIdFromName(item.name);
        if (extractedId) {
            id = extractedId;
        }
    }
    
    // Если ID всё ещё невалидный — возвращаем элемент как есть (не теряем данные)
    return {
        ...item,
        id: String(id),
    };
};

/**
 * Нормализует фильтры для отправки на сервер.
 * Исправляет невалидные ID, извлекая их из name если возможно.
 */
const normalizeFiltersForRequest = (filters: any[]): any[] => {
    if (!Array.isArray(filters)) return filters;
    
    return filters.map((filter) => {
        if (!filter) return filter;
        
        const normalizedFilter = { ...filter };
        
        if (Array.isArray(filter.items)) {
            normalizedFilter.items = filter.items.map(normalizeFilterItem);
        }
        
        if (Array.isArray(filter.checkeds)) {
            normalizedFilter.checkeds = filter.checkeds.map(normalizeFilterItem);
        }
        
        return normalizedFilter;
    });
};

// ==================== ТИПЫ ====================

export type ReportType = 'registration' | 'order' | 'payment' | 'ad' | 'webinar' | 'visit';

/**
 * Режим отображения графика.
 * - 'full' — полный режим (страница отчёта)
 * - 'compact' — компактный режим (блок дашборда)
 * - 'preview' — превью (модальное окно)
 */
export type ChartDisplayMode = 'full' | 'compact' | 'preview';

interface ReportChartProps {
    /** Тип отчёта */
    reportType: ReportType;
    /** ID блока дашборда (для режимов compact/preview) */
    blockId?: string;
    /** Режим отображения */
    displayMode?: ChartDisplayMode;
    /** Режим модального окна (для работы с dashboardModal state) */
    isModal?: boolean;
    /** Дата блока дашборда (формат: "type_start_end") */
    date?: string;
    /** Callback для открытия полного отчёта */
    onOpenFullReport?: () => void;
    /** Скрыть кнопки выбора типа графика */
    hideChartTypeButtons?: boolean;
    /** Скрыть заголовок графика */
    hideTitle?: boolean;
    /** Высота графика (по умолчанию 450) */
    chartHeight?: number;
    /** Переопределить период (игнорировать глобальный период из Redux) */
    overridePeriod?: { start: string; end: string; withTime?: boolean };
}

interface CompareLoadingItem {
    time: number;
    loading: boolean;
}

interface MetricItem {
    name: string;
    label?: string | { props?: { children?: string[] } };
    dataKey?: string;
    plan?: number;
    [key: string]: unknown;
}

interface GroupingItem {
    dataKey: string;
    name?: string;
    [key: string]: unknown;
}

interface GroupCheckedItem {
    pathStr: string;
    ids?: string[];
    [key: string]: unknown;
}

interface SeriesItem {
    name: string;
    dataKey?: string;
    data: number[];
    type?: string;
    color?: string;
    yAxisIndex?: number;
    plan?: number;
    value?: number;
    [key: string]: unknown;
}

interface ChartDataItem {
    x: string | number;
    y: number;
    [key: string]: unknown;
}

interface RequestData {
    period: { start: string; end: string };
    filters: unknown[];
    attribution: { attribution: string | string[]; type_applications: string | string[] };
    // Группы регистрации схлопываются во вложенный массив (см. buildGroupingsNames)
    groupings_names: Array<string | string[]>;
    metrics_names: string[];
    details: string;
    expanded_ids: string[];
    sort: Record<string, unknown>;
    percentages: unknown;
    [key: string]: unknown;
}

// ==================== КОНСТАНТЫ ====================

const CHART_HEIGHT = 450;
const MAX_COMPARE_LOADING_ITEMS = 10; // Ограничение размера массива compareLoading для предотвращения утечки памяти

/**
 * Добавляет новый элемент в compareLoading с ограничением размера массива
 */
const addCompareLoadingItem = (prev: CompareLoadingItem[], newItem: CompareLoadingItem): CompareLoadingItem[] => {
    const updated = [...prev, newItem];
    // Оставляем только последние MAX_COMPARE_LOADING_ITEMS элементов
    if (updated.length > MAX_COMPARE_LOADING_ITEMS) {
        return updated.slice(-MAX_COMPARE_LOADING_ITEMS);
    }
    return updated;
};

/**
 * Обновляет элемент в compareLoading по времени с ограничением размера массива
 */
const updateCompareLoadingItem = (prev: CompareLoadingItem[], time: number, loading: boolean): CompareLoadingItem[] => {
    const filtered = prev.filter((val) => val.time !== time);
    const updated = [...filtered, { time, loading }];
    // Оставляем только последние MAX_COMPARE_LOADING_ITEMS элементов
    if (updated.length > MAX_COMPARE_LOADING_ITEMS) {
        return updated.slice(-MAX_COMPARE_LOADING_ITEMS);
    }
    return updated;
};

const X_AXIS_LABELS: Record<ReportType, string> = {
    registration: 'Дата регистрации',
    order: 'Дата заказа',
    payment: 'Дата платежа',
    ad: 'Дата рекламы',
    visit: 'Дата рекламы',
    webinar: 'Дата вебинара',
};

// ==================== УТИЛИТЫ ====================

const getXAxisLabel = (reportType: ReportType): string => X_AXIS_LABELS[reportType] || '';

const getMetricLabel = (metric: MetricItem): string => {
    if (typeof metric.label === 'object' && metric.label?.props?.children?.[0]) {
        return metric.label.props.children[0];
    }
    return String(metric.label || '');
};

// ==================== КОМПОНЕНТ ====================

const ReportChart: FC<ReportChartProps> = ({ 
    reportType, 
    blockId,
    displayMode = 'full',
    isModal = false,
    date,
    onOpenFullReport,
    hideChartTypeButtons = false,
    hideTitle = false,
    chartHeight,
    overridePeriod,
}) => {
    const location = useLocation();
    const titles = useTitles();
    const abortControllerRef = useRef<AbortController | null>(null);
    
    // Определяем контекст использования
    // isDashboardBlock: блок дашборда (compact режим) ИЛИ модальное окно с blockId (full режим для просмотра блока)
    const isDashboardBlock = !!blockId && (displayMode !== 'full' || (isModal && displayMode === 'full'));
    const isCompactMode = displayMode === 'compact' || displayMode === 'preview';
    // Флаг: модальное окно для просмотра блока дашборда (FullReportModal)
    const isBlockViewModal = isModal && !!blockId && displayMode === 'full';
    // Флаг: модальное окно редактирования/добавления/копирования блока (preview режим без blockId)
    const isEditingModal = isModal && !blockId && displayMode === 'preview';
    
    // Получаем настройки блока через централизованный хук
    const blockSettings = useDashboardBlockSettings(blockId, { 
        isModal: isModal && !isBlockViewModal, // Для FullReportModal используем настройки блока, не модальные
        skip: !blockId 
    });
    
    // Redux state с учётом контекста
    const globalPeriod = useTypedSelector((state) => state.period);
    // Используем переопределённый период если передан, иначе глобальный
    const period = overridePeriod || globalPeriod;
    
    // Детализация: для блоков дашборда используем blockSettings, иначе глобальный state
    const details = useTypedSelector((state) => {
        if (isDashboardBlock) {
            return blockSettings.details;
        }
        if (isModal) {
            return state.details.details;
        }
        return state.details.details;
    });
    
    // Фильтры: для блоков используем блочные, для модалок — dashboardModal
    const { filters } = useTypedSelector((state) => {
        if (isDashboardBlock && blockId) {
            return state.filter.blocks[blockId] ?? { filters: [] };
        }
        if (isModal) {
            return state.filter.dashboardModal;
        }
        return state.filter;
    });
    
    // Общие фильтры дашборда (для мержа с блочными)
    // Используем blockSettings.globalFilters напрямую — он уже реактивно обновляется через useDashboardBlockSettings
    const commonFilters = isDashboardBlock ? blockSettings.globalFilters : [];
    
    // Атрибуты: для блоков дашборда используем blockSettings
    const attributes = useTypedSelector((state) => {
        if (isDashboardBlock) {
            return {
                attribution: blockSettings.attribution,
                type_applications: blockSettings.type_applications,
            };
        }
        if (isModal) {
            return state.attributes.attributes;
        }
        return state.attributes.attributes;
    });
    
    // Данные таблицы: для блоков используем блочный state, для модальных окон — глобальный
    // ВАЖНО: для модальных окон редактирования (isEditingModal) используем глобальный state.table,
    // т.к. данные записываются туда через setMetrics/setGroupings
    const {
        groupings: rawGroupings,
        tableData,
        percentages,
        sortTable,
        groupsChecked,
        metricsChecked,
        totalChecked,
    } = useTypedSelector((state) => {
        // Для модальных окон редактирования используем глобальный state
        if (isEditingModal) {
            return state.table;
        }
        // Для блоков дашборда используем блочный state
        if (isDashboardBlock && blockId) {
            const blockState = state.table.blocks[blockId];
            return blockState ?? state.table;
        }
        return state.table;
    });
    
    // Фильтруем undefined элементы для защиты от ошибок
    // Используем useMemo чтобы избежать создания нового массива при каждом рендере
    const groupings = useMemo(() => (rawGroupings ?? []).filter(Boolean), [rawGroupings]);
    // Фильтр по группам регистраций из текущего скоупа — чистит ось так же, как бэк.
    const groupFilter = useMemo(() => resolveGroupAxisFilter(filters), [filters]);
    
    // Данные графика: для блоков используем блочный state, для модальных окон — глобальный
    const { chartType, series, isChartVisible, isAxesVisible, showTrend: globalShowTrend, defaultTrendType } = useTypedSelector((state) => {
        // Для модальных окон редактирования используем глобальный state
        if (isEditingModal) {
            return state.chart;
        }
        // Для блоков дашборда используем блочный state
        if (isDashboardBlock && blockId) {
            return state.chart.blocks[blockId] ?? state.chart;
        }
        return state.chart;
    });
    
    // Мета-состояние: для блоков используем блочный state
    const {
        isCompare,
        isChartLoading,
        isTableLoading,
        isChartCompareFetching,
        isChartFetching,
    } = useTypedSelector((state) => {
        if (blockId) {
            return state.meta.blocks[blockId] ?? state.meta;
        }
        return state.meta;
    });
    
    // Флаги загрузки для модального окна
    const isModalChartFetching = useTypedSelector((state) => state.meta.isModalChartFething);
    
    const { compareFilters, comparePeriod } = useTypedSelector((state) => state.compare);

    // Actions
    const {
        setSeries: setSeriesAction,
        setLabels: setLabelsAction,
        setMetaChartLoading: setMetaChartLoadingAction,
        setTotalChecked,
        setGroupingsChecked,
        setMetricsChecked,
        setMetaChartFetching,
        setDetails,
        setMetaCompareChartFetching,
        setAllBlocksFetching,
        setMetaFilterUpdate,
        setAllDataFetching,
        setMetaModalChartFetching,
    } = useActions();
    
    // Обёртки для actions, которые автоматически добавляют blockId для блоков дашборда
    const setSeries = useCallback((data: any) => {
        if (isDashboardBlock && blockId) {
            setSeriesAction({ data, blockId });
        } else {
            setSeriesAction(data);
        }
    }, [isDashboardBlock, blockId, setSeriesAction]);
    
    const setLabels = useCallback((data: any) => {
        if (isDashboardBlock && blockId) {
            setLabelsAction({ data, blockId });
        } else {
            setLabelsAction(data);
        }
    }, [isDashboardBlock, blockId, setLabelsAction]);
    
    const setMetaChartLoading = useCallback((params: { value: boolean }) => {
        if (isDashboardBlock && blockId) {
            setMetaChartLoadingAction({ ...params, blockId });
        } else {
            setMetaChartLoadingAction(params);
        }
    }, [isDashboardBlock, blockId, setMetaChartLoadingAction]);
    
    // Эффективная высота графика
    const effectiveChartHeight = chartHeight ?? (isCompactMode ? 300 : CHART_HEIGHT);

    // Local state
    const [data, setData] = useState<Record<string, any>>({});
    const [_metricsChecked, _setMetricsChecked] = useState<any[]>([]);
    const [compareLoading, setCompareLoading] = useState<CompareLoadingItem[]>([
        { time: 0, loading: false },
    ]);
    
    // Ref для отслеживания предыдущего типа графика (исправление race condition)
    const prevChartTypeRef = useRef(chartType);
    // Ref для compareLoading чтобы избежать его в зависимостях useEffect
    const compareLoadingRef = useRef(compareLoading);
    compareLoadingRef.current = compareLoading;

    const { modalState2 } = useModalState();

    // ==================== МЕМОИЗИРОВАННЫЕ ВЫЧИСЛЕНИЯ ====================

    const seriesForBubbleChart = useMemo(
        () => buildSeriesForBubbleChart(groupsChecked, totalChecked, chartType, tableData, metricsChecked),
        [metricsChecked, chartType, groupsChecked, totalChecked, tableData]
    );

    const offsetsBubbleChart = useMemo(
        () => calculateMaxMinBubbleChart(seriesForBubbleChart),
        [seriesForBubbleChart]
    );

    const xAxisLabel = useMemo(() => getXAxisLabel(reportType), [reportType]);

    // Формируем настройки отображения метрик для графика (showTrend, hideLine, trendType)
    // Глобальный showTrend перебивает индивидуальные настройки
    const metricDisplaySettings: MetricDisplaySettings[] = useMemo(() => {
        return _metricsChecked.map((metric: any) => ({
            dataKey: metric.dataKey || metric.name || '',
            showTrend: globalShowTrend === false ? false : metric.showTrend !== false,
            hideLine: metric.hideLine === true,
            trendType: metric.trendType || undefined,
        }));
    }, [_metricsChecked, globalShowTrend]);

    // Подготовленные данные для графиков (pie, bar, funnel, etc.)
    const preparedSeriesData = useMemo(() => {
        const updatedSeries = series.map((item: any) => ({
            ...item,
            group: typeof item.group === 'string' ? cleanHtmlTags(item.group) : '',
            value: item.value != null && !isNaN(item.value) ? parseInt(String(item.value), 10) : 0,
        }));

        const sortValue = Object.values(sortTable)[0];
        const sortedSeries = [...updatedSeries];
        if (sortValue === 'ASC') {
            sortedSeries.sort((a, b) => a.value - b.value);
        } else if (sortValue === 'DESC') {
            sortedSeries.sort((a, b) => b.value - a.value);
        }

        const colorMap = new Map<string, string>();
        let colorIndex = 0;
        const coloredSeries = updatedSeries.map((item) => {
            if (!colorMap.has(item.group)) {
                colorMap.set(item.group, CHART_COLORS[colorIndex % CHART_COLORS.length]);
                colorIndex++;
            }
            return { ...item, color: colorMap.get(item.group) };
        });

        const isNullableData = updatedSeries.every((item) => !item.value);

        return { updatedSeries, sortedSeries, coloredSeries, isNullableData };
    }, [series, sortTable]);

    // ==================== ЭФФЕКТЫ ====================

    // Отмена запросов при размонтировании
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Обработка смены типа графика (исправлен race condition с prevChartType)
    useEffect(() => {
        // Проверяем предыдущее значение ДО обновления ref
        if (prevChartTypeRef.current === 'bubble' && chartType !== 'bubble') {
            setTotalChecked();
            setGroupingsChecked([]);
            setMetricsChecked({ data: [_metricsChecked[0]] });
            _setMetricsChecked([_metricsChecked[0]]);
        }
        // Обновляем ref ПОСЛЕ проверки
        prevChartTypeRef.current = chartType;
    }, [chartType]);

    // Управление состоянием загрузки
    // Синхронизируем compareLoading с isChartLoading только когда isChartLoading сбрасывается извне
    // Используем ref для compareLoading чтобы избежать лишних перерисовок
    useEffect(() => {
        const hasLoading = compareLoadingRef.current.some((item) => item.loading);
        
        if (!isChartLoading && hasLoading) {
            setCompareLoading((prev) => prev.map((item) => ({ ...item, loading: false })));
        }
    }, [isChartLoading]);

    // Синхронизация локальных метрик с Redux
    useEffect(() => {
        // Фильтруем undefined и элементы без dataKey
        const validMetricsChecked = (metricsChecked ?? []).filter((m: any) => m && (m.dataKey || m.name));
        
        if (isCompare) {
            const newMetrics: any[] = [];
            validMetricsChecked.forEach((metric: any) => {
                const baseKey = getMetricDataKey(metric);
                if (!baseKey) return;
                const newMetricLabel = getMetricLabel(metric);
                const compareKey = toCompareMetricKey(baseKey);
                const newMetric = {
                    dataKey: compareKey,
                    name: compareKey,
                    label: `${newMetricLabel} (2)`,
                    // Настройки отображения наследуются от основной метрики: скрытие линии
                    // и тренд должны работать сразу на обоих периодах сравнения
                    hideLine: metric.hideLine,
                    showTrend: metric.showTrend,
                    trendType: metric.trendType,
                    base_metric_name: metric.base_metric_name,
                    metric_formula: metric.metric_formula,
                    unit: metric.unit,
                };
                if (!hasMetricByDataKey(validMetricsChecked, compareKey)) {
                    newMetrics.push(newMetric);
                }
            });
            _setMetricsChecked([...validMetricsChecked, ...newMetrics]);
        } else {
            _setMetricsChecked(validMetricsChecked);
        }
    }, [isCompare, metricsChecked]);

    // Определяем, показывать ли график (скрываем если нет выбранных метрик, но не меняем настройки пользователя)
    // Для блоков дашборда isChartVisible всегда true, т.к. блок типа "chart" должен показывать график
    const effectiveChartVisible = isDashboardBlock ? true : isChartVisible;
    const shouldShowChart = effectiveChartVisible && metricsChecked.length > 0;

    // Ref для проверки видимости внутри debounced функций (чтобы не отправлять запрос при скрытом графике)
    const chartVisibleRef = useRef(effectiveChartVisible);
    useEffect(() => {
        chartVisibleRef.current = effectiveChartVisible;
    }, [effectiveChartVisible]);

    // Ограничение метрик для некоторых типов графиков
    useEffect(() => {
        if (_metricsChecked.length > 1 && ['pie', 'area', 'treemap'].includes(chartType)) {
            // В сравнении последними в списке идут служебные метрики второго периода —
            // в Redux они попасть не должны, оставляем последнюю обычную метрику
            const realMetrics = _metricsChecked.filter(
                (metric: any) => !isCompareMetricKey(getMetricDataKey(metric))
            );
            setMetricsChecked({ data: realMetrics.slice(-1) });
        }
    }, [chartType, _metricsChecked]);

    // ==================== ЗАПРОСЫ ДАННЫХ ====================

    const fetchCompareDataInternal = useCallback(() => {
        // Если график скрыт — не отправляем запрос
        if (!chartVisibleRef.current) {
            setMetaChartLoading({ value: false });
            return;
        }

        const time = Date.now();

        // Отменяем предыдущий запрос
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setMetaChartLoading({ value: true });
        setCompareLoading((prev) => addCompareLoadingItem(prev, { time, loading: true }));

        const { compareFilters1, compareFilters2 } = compareFilters;
        const { comparePeriod1, comparePeriod2 } = comparePeriod;

        const groupingsNames = buildGroupingsNames(groupings, groupFilter, groupSuffixByReportType(reportType));
        let selectedGroupsPath = groupsChecked.map((item: any) => item?.ids).filter(Boolean);
        selectedGroupsPath = selectedGroupsPath.filter((el) => !el.includes('total'));
        selectedGroupsPath.push(['total']);
        // Служебные имена второго периода серверу не нужны: он сам считает оба периода
        // и возвращает второй с суффиксом сравнения. Отправлять их — значит просить
        // посчитать метрику, которой нет в справочнике
        const selectedMetricsNames = _metricsChecked
            .map((item) => getMetricDataKey(item))
            .filter((dataKey) => Boolean(dataKey) && !isCompareMetricKey(dataKey));

        const reqData = {
            comparePeriod1: {
                start: formatPeriodDate(comparePeriod1.start, period.withTime),
                end: formatPeriodDate(comparePeriod1.end, period.withTime),
            },
            comparePeriod2: {
                start: formatPeriodDate(comparePeriod2.start, period.withTime),
                end: formatPeriodDate(comparePeriod2.end, period.withTime),
            },
            compareFilters1: normalizeFiltersForRequest(compareFilters1),
            compareFilters2: normalizeFiltersForRequest(compareFilters2),
            attribution: {
                attribution: attributes?.attribution,
                type_applications: attributes?.type_applications,
            },
            details,
            groupings_names: groupingsNames,
            selected_groups_path: selectedGroupsPath,
            selected_metrics_names: selectedMetricsNames,
        };

        unApi
            .getUniversalReportChartData(reqData, location.pathname)
            .then((response) => {
                const respData = response.data;
                // Битый отчёт: ссылка на удалённую метрику — показываем причину и починку
                if (isDeletedMetricsError(respData)) {
                    handleDeletedMetricsError(respData);
                    setCompareLoading((prev) => updateCompareLoadingItem(prev, time, false));
                    setMetaChartLoading({ value: false });
                    return;
                }
                // Битый отчёт: ссылка на удалённую группу регистраций (код 255)
                if (isDeletedGroupsError(respData)) {
                    handleDeletedGroupsError(respData);
                    setCompareLoading((prev) => updateCompareLoadingItem(prev, time, false));
                    setMetaChartLoading({ value: false });
                    return;
                }
                setData(respData.status ? {} : respData.data);
                setCompareLoading((prev) => updateCompareLoadingItem(prev, time, false));
                setMetaChartLoading({ value: false });
            })
            .catch(() => {
                setCompareLoading((prev) => updateCompareLoadingItem(prev, time, false));
                setMetaChartLoading({ value: false });
            })
            .finally(() => {
                setAllBlocksFetching({ value: false });
                setMetaFilterUpdate({ value: false });
                setAllDataFetching({ value: false });
            });
    }, [compareFilters, comparePeriod, groupings, groupsChecked, _metricsChecked, attributes, details, location.pathname]);

    const fetchDataInternal = useCallback((metrics: any[]) => {
        // Если график скрыт — не отправляем запрос
        if (!chartVisibleRef.current) {
            setMetaChartLoading({ value: false });
            return;
        }

        const time = Date.now();

        // Валидация данных перед запросом
        if (!validateChartRequest(groupings, period, 'ReportChart.fetchDataInternal')) {
            setMetaChartLoading({ value: false });
            return;
        }

        // Отменяем предыдущий запрос
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setMetaChartLoading({ value: true });
        setCompareLoading((prev) => addCompareLoadingItem(prev, { time, loading: true }));

        const _period = {
            start: formatPeriodDate(period.start, period.withTime),
            end: formatPeriodDate(period.end, period.withTime),
        };
        const groupingsNames = buildGroupingsNames(groupings, groupFilter, groupSuffixByReportType(reportType));

        let selectedGroupsPath = groupsChecked.map((item: any) => item?.ids).filter(Boolean);
        selectedGroupsPath = selectedGroupsPath.filter((el) => !el.includes('total'));
        selectedGroupsPath.push(['total']);
        // Служебные имена второго периода сюда попадать не должны: при выходе из сравнения
        // список метрик пересчитывается не мгновенно, а обычному отчёту они не нужны
        const selectedMetricsNames = metrics
            .map((item) => getMetricDataKey(item))
            .filter((dataKey) => Boolean(dataKey) && !isCompareMetricKey(dataKey));

        if (chartType === 'bubble') {
            setData([]);
            setMetaChartLoading({ value: false });
            return;
        }

        if (!details || selectedMetricsNames.length === 0) {
            setCompareLoading((prev) => updateCompareLoadingItem(prev, time, false));
            return;
        }

        let reqData: any = {
            period: _period,
            details,
            filters: mergeAndBuildApiFilters({
                blockFilters: normalizeFiltersForRequest(filters),
                globalDashboardFilters: commonFilters,
                isDashboardBlock: isDashboardBlock,
            }),
            attribution: {
                attribution: typeof attributes?.attribution === 'string'
                    ? [attributes.attribution]
                    : attributes?.attribution,
                type_applications: typeof attributes?.type_applications === 'string'
                    ? [attributes.type_applications]
                    : attributes?.type_applications,
            },
            groupings_names: groupingsNames,
            selected_groups_path: selectedGroupsPath,
            selected_metrics_names: selectedMetricsNames,
        };

        const dotsCount = predictDotsCount(
            reqData.period,
            reqData.selected_groups_path.length,
            reqData.selected_metrics_names.length,
            reqData.details
        );

        if (dotsCount > 1000) {
            const newDetails = dotsCount > 3000 ? 'm' : appendDetails(reqData.details);
            reqData.details = newDetails;
            setDetails(newDetails);
        }

        unApi
            .getUniversalReportChartData(reqData, location.pathname)
            .then((response) => {
                if (response.status === 200) {
                    const respData = response.data;
                    // Битый отчёт: ссылка на удалённую метрику — показываем причину и починку
                    if (isDeletedMetricsError(respData)) {
                        handleDeletedMetricsError(respData);
                        setCompareLoading((prev) => updateCompareLoadingItem(prev, time, false));
                        setMetaChartLoading({ value: false });
                        return;
                    }
                    // Битый отчёт: ссылка на удалённую группу регистраций (код 255)
                    if (isDeletedGroupsError(respData)) {
                        handleDeletedGroupsError(respData);
                        setCompareLoading((prev) => updateCompareLoadingItem(prev, time, false));
                        setMetaChartLoading({ value: false });
                        return;
                    }
                    setData(respData.status ? {} : respData.data);
                } else {
                    setData({});
                }
                setCompareLoading((prev) => updateCompareLoadingItem(prev, time, false));
                setMetaChartLoading({ value: false });
            })
            .catch(() => {
                setData({});
                setCompareLoading((prev) => updateCompareLoadingItem(prev, time, false));
                setMetaChartLoading({ value: false });
            })
            .finally(() => {
                setAllBlocksFetching({ value: false });
                setMetaFilterUpdate({ value: false });
                setAllDataFetching({ value: false });
            });
    }, [period, groupings, groupsChecked, chartType, details, filters, commonFilters, isDashboardBlock, attributes, location.pathname]);

    // Debounced версии функций для предотвращения частых запросов
    const debouncedFetchData = useDebounce(fetchDataInternal, 300);
    const debouncedFetchCompareData = useDebounce(fetchCompareDataInternal, 300);

    // Обёртки с debounce для предотвращения частых запросов
    const fetchData = useCallback((metrics: any[]) => {
        debouncedFetchData(metrics);
    }, [debouncedFetchData]);

    const fetchCompareData = useCallback(() => {
        debouncedFetchCompareData();
    }, [debouncedFetchCompareData]);

    // Для модальных окон используем isModalChartFetching вместо isChartFetching
    const effectiveChartFetching = isEditingModal ? isModalChartFetching : isChartFetching;
    
    // Триггер загрузки данных
    // ВАЖНО: используем metricsChecked из Redux для проверки условия, а _metricsChecked для передачи в fetchData
    // Это нужно потому что _metricsChecked синхронизируется через useEffect с задержкой
    useEffect(() => {
        // Для типов графиков treemap, bar, pie данные берутся из таблицы, не требуется отдельный запрос
        // Сбрасываем isChartLoading если он был установлен
        if (chartType === 'bar' || chartType === 'pie' || chartType === 'treemap') {
            setMetaChartLoading({ value: false });
            return;
        }

        // Если график скрыт — не отправляем запросы, сбрасываем все флаги загрузки
        if (!effectiveChartVisible) {
            // Всегда сбрасываем isChartLoading, чтобы лоадер не крутился при скрытом графике
            setMetaChartLoading({ value: false });
            if (effectiveChartFetching) {
                if (isDashboardBlock && blockId) {
                    setMetaChartFetching({ value: false, blockId });
                } else {
                    setMetaChartFetching({ value: false });
                }
                if (isEditingModal) {
                    setMetaModalChartFetching({ value: false });
                }
            }
            if (isChartCompareFetching) {
                setMetaCompareChartFetching({ value: false });
            }
            return;
        }

        // Проверяем metricsChecked из Redux (не _metricsChecked), т.к. _metricsChecked синхронизируется с задержкой
        if ((groupsChecked.length === 0 && totalChecked.length === 0) || metricsChecked.length === 0) {
            return;
        }

        // Ждём пока _metricsChecked синхронизируется с metricsChecked
        // НЕ сбрасываем флаг fetching, чтобы при следующем рендере загрузка произошла
        if (_metricsChecked.length === 0 && metricsChecked.length > 0) {
            return;
        }

        if (isCompare) {
            if (!isChartCompareFetching) return;
            fetchCompareData();
            setMetaCompareChartFetching({ value: false });
        } else {
            if (!effectiveChartFetching) return;
            fetchData(_metricsChecked);
            // Сбрасываем флаг fetching с blockId для блоков дашборда
            if (isDashboardBlock && blockId) {
                setMetaChartFetching({ value: false, blockId });
            } else {
                setMetaChartFetching({ value: false });
            }
            if (isEditingModal) {
                setMetaModalChartFetching({ value: false });
            }
        }
    }, [chartType, effectiveChartFetching, effectiveChartVisible, isChartCompareFetching, groupsChecked, totalChecked, metricsChecked, _metricsChecked, fetchData, fetchCompareData, isEditingModal, isDashboardBlock, blockId]);

    // При включении графика — загрузить данные, если их ещё нет
    const prevChartVisibleRef = useRef(effectiveChartVisible);
    useEffect(() => {
        const wasHidden = !prevChartVisibleRef.current;
        prevChartVisibleRef.current = effectiveChartVisible;

        if (wasHidden && effectiveChartVisible && _metricsChecked.length > 0 && (groupsChecked.length > 0 || totalChecked.length > 0)) {
            // График только что стал видимым — загружаем данные
            fetchData(_metricsChecked);
        }
    }, [effectiveChartVisible, _metricsChecked, groupsChecked, totalChecked, fetchData]);

    // ==================== ОБРАБОТКА ДАННЫХ ДЛЯ SERIES ====================

    // Объединённые группы для обработки (фильтруем undefined и невалидные элементы)
    const allGroupsChecked = useMemo(() => {
        const checked = [...(groupsChecked ?? []), ...(totalChecked ?? [])].filter((g: any) => g && g.pathStr);

        if (checked.length > 0) return checked;

        // Для блоков дашборда с table-dependent типами графиков:
        // если нет выбранных групп, автоматически используем строки из tableData
        const tableChartTypes = ['bar', 'pie', 'treemap', 'tunnel', 'speedometer'];
        if (isDashboardBlock && tableChartTypes.includes(chartType) && tableData?.data?.length > 0) {
            return tableData.data
                .filter((row: any) => row.pathStr && row.pathStr !== 'total' && row.pathStr !== 'total_choosen')
                .map((row: any) => ({
                    ids: row.clientData?.path || [],
                    pathStr: row.pathStr || '',
                    label: row.clientData?.pathLabel?.join(' → ') || row.group || '',
                }));
        }

        return checked;
    }, [groupsChecked, totalChecked, isDashboardBlock, chartType, tableData]);

    // Проверка валидности данных
    const isDataValid = useMemo(() => {
        if (!data) return false;
        if (allGroupsChecked.length === 0 || _metricsChecked.length === 0) return false;
        if (['line', 'area'].includes(chartType) && Object.keys(data).length === 0) return false;
        return true;
    }, [data, allGroupsChecked.length, _metricsChecked.length, chartType]);

    // Обработка данных для PIE графика
    const pieSeries = useMemo(() => {
        if (chartType !== 'pie' || !isDataValid) return null;
        
        const metricFirstName = getMetricDataKey(_metricsChecked[0]);
        if (!metricFirstName) return null;

        // Используем groupsChecked если есть, иначе allGroupsChecked (с fallback из tableData)
        const pieGroups = groupsChecked.length > 0 ? groupsChecked : allGroupsChecked;

        const series = pieGroups.filter(Boolean).map((group: any) => {
            const findRow = findRowRecurse(tableData.data, group?.pathStr);
            return {
                group: group?.label,
                value: findRow ? Number(findRow[metricFirstName]) ?? 0 : 0,
            };
        });

        const sumSeries = series.reduce((acc, item) => acc + item.value, 0);
        const remainingValue = (tableData?.total?.[metricFirstName] ?? 0) - sumSeries;

        return [
            ...(remainingValue > 0 ? [{ group: 'Остальные', value: remainingValue }] : []),
            ...series,
        ];
    }, [chartType, isDataValid, _metricsChecked, groupsChecked, allGroupsChecked, tableData]);

    // Обработка данных для BAR графика
    const barSeries = useMemo(() => {
        if (chartType !== 'bar' || !isDataValid) return null;

        const series: any[] = [];
        _metricsChecked.forEach((metric) => {
            const metricLabel = getMetricLabel(metric);
            allGroupsChecked.forEach((group) => {
                const val = getGroupDataForBarChart(isCompare, tableData, percentages, metric, group);
                series.push({
                    key: metricLabel,
                    group: group.label.replace(/&nbsp;/g, ' '),
                    value: val,
                });
            });
        });
        return series;
    }, [chartType, isDataValid, _metricsChecked, allGroupsChecked, isCompare, tableData, percentages]);

    // Обработка данных для TUNNEL (воронка) графика
    const tunnelSeries = useMemo(() => {
        if (chartType !== 'tunnel' || !isDataValid) return null;

        const colors = ['#339dff', '#00b379', '#ffc233', '#d52f40', '#5d3faa'];
        const metricColorMap = new Map<string, string>();
        let colorIndex = 0;
        const series: any[] = [];

        const filteredGroups = _metricsChecked.length === 1 ? [allGroupsChecked[0]] : allGroupsChecked;

        _metricsChecked.forEach((metric) => {
            const metricLabel = getMetricLabel(metric);
            if (!metricColorMap.has(metricLabel)) {
                metricColorMap.set(metricLabel, colors[colorIndex % colors.length]);
                colorIndex++;
            }

            filteredGroups.forEach((group: any) => {
                const val = getGroupDataForBarChart(isCompare, tableData, percentages, metric, group);
                series.push({
                    key: group.label.replace(/&nbsp;/g, ' '),
                    metric: metricLabel,
                    value: val,
                    color: metricColorMap.get(metricLabel),
                });
            });
        });

        return series;
    }, [chartType, isDataValid, _metricsChecked, allGroupsChecked, isCompare, tableData, percentages]);

    // Обработка данных для SPEEDOMETER графика
    const speedometerSeries = useMemo(() => {
        if (chartType !== 'speedometer' || !isDataValid) return null;

        let metricsToUse = _metricsChecked;
        let groupsToUse = allGroupsChecked;

        if (metricsToUse.length > 1) metricsToUse = [metricsToUse[0]];
        if (groupsToUse.length > 1) groupsToUse = [groupsToUse[0]];

        if (metricsToUse.length === 0) return null;

        const selectedMetric = metricsToUse[0];
        const selectedGroup = groupsToUse[0];
        const metricLabel = getMetricLabel(selectedMetric);
        const val = getGroupDataForBarChart(isCompare, tableData, percentages, selectedMetric, selectedGroup);

        return [{ metric: metricLabel, value: val }];
    }, [chartType, isDataValid, _metricsChecked, allGroupsChecked, isCompare, tableData, percentages]);

    // Обработка данных для TREEMAP графика
    const treemapSeries = useMemo(() => {
        if (chartType !== 'treemap' || !isDataValid) return null;

        const treemapMetricName = getMetricDataKey(_metricsChecked[0]);
        const treemapMetricLabel = _metricsChecked[0]?.label;

        if (!treemapMetricName) return null;

        const treemapChildren: Array<{ name: string; value: number }> = [];

        allGroupsChecked.forEach((group: any) => {
            const findRow = findRowRecurse(tableData.data, group?.pathStr);
            if (!findRow) return;

            const val = Number(findRow[treemapMetricName]) || 0;
            if (val <= 0) return;

            let groupLabel = group.label;
            if (typeof groupLabel === 'string') {
                groupLabel = groupLabel.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, ' ');
            }

            treemapChildren.push({ name: groupLabel, value: val });
        });

        return [{
            name: typeof treemapMetricLabel === 'object'
                ? (treemapMetricLabel as any).props?.children?.[0] || 'Метрика'
                : treemapMetricLabel || 'Метрика',
            children: treemapChildren,
        }];
    }, [chartType, isDataValid, _metricsChecked, allGroupsChecked, tableData]);

    // Обработка данных для LINE/AREA графиков
    const timeSeriesData = useMemo(() => {
        if (!['line', 'area'].includes(chartType) || !isDataValid) return null;

        let newSeries: any[] = [];
        let newYaxis: any[] = [];
        const localCheckedMetrics: string[] = [];

        _metricsChecked.forEach((metric) => {
            const metricKey = getMetricDataKey(metric);
            if (!metricKey) return;
            allGroupsChecked.forEach((group) => {
                const metricLabel = typeof metric.label === 'string' ? metric.label : 'Метрика';
                const groupLabel = typeof group.label === 'string' ? group.label : 'группа';
                const name = `${metricLabel} (${groupLabel})`;

                const groupData = data[group.pathStr];
                if (!groupData) return;

                // Второй период сравнения: пока бэкенд не обновлён, он отдаёт данные
                // под старым именем (orders_count_2), а не под служебным (orders_count__cmp)
                const responseKey = (!groupData[metricKey] && isCompareMetricKey(metricKey))
                    ? toLegacyCompareMetricKey(metricKey)
                    : metricKey;

                const points = groupData[responseKey];
                if (!points) return;

                let dataResult = points.map((item: any) => ({
                    ...item,
                    category: name,
                }));

                if (percentages.includes(metricKey as any)) {
                    dataResult = dataResult.map((item: any, index: number) => {
                        const totalVal = data.total?.[responseKey]?.[index]?.y;
                        if (!totalVal) return item;
                        const y = parseFloat(String((item.y / totalVal) * 100)).toFixed(2);
                        return { ...item, y };
                    });
                }

                if (chartType === 'line') {
                    if (!localCheckedMetrics.includes(metricKey)) {
                        localCheckedMetrics.push(metricKey);
                    }

                    const _group = checkMetricsGroups(metric, name, group.label, localCheckedMetrics);
                    newYaxis.push({
                        seriesName: _group.label,
                        show: _group.show,
                        opposite: true,
                        isCustomMetric: metricKey.startsWith('_'),
                        dataKey: metricKey,
                    });
                    newSeries.push({
                        name,
                        data: dataResult,
                        dataKey: metricKey,
                        plan: metric?.plan,
                        label: metric?.label as string,
                        base_metric_name: metric?.base_metric_name,
                        metric_formula: metric?.metric_formula,
                        axisGroup: (metric as any)?.axisGroup,
                    });
                } else {
                    newSeries.push({ 
                        name, 
                        data: dataResult,
                        dataKey: metricKey,
                        axisGroup: (metric as any)?.axisGroup,
                    } as any);
                }
            });
        });

        // Добавляем план если есть
        let planSeries: any[] = [];
        newSeries.forEach((el: any) => {
            if (isNumeric(el?.plan as any)) {
                const plan = getPlanSeries(el as any, details, el?.dataKey?.endsWith('cumulative'));
                planSeries.push(plan);
            }
        });

        // Удаляем дубликаты (оптимизировано: O(n) вместо O(n²))
        const seenSeries = new Map<string, boolean>();
        newSeries = newSeries.filter((el: any) => {
            const key = `${el?.dataKey || ''}:${el?.name || ''}`;
            if (seenSeries.has(key)) return false;
            seenSeries.set(key, true);
            return true;
        });
        
        const seenPlanSeries = new Map<string, boolean>();
        planSeries = planSeries.filter((el: any) => {
            const key = `${el?.dataKey || ''}:${el?.name || ''}`;
            if (seenPlanSeries.has(key)) return false;
            seenPlanSeries.set(key, true);
            return true;
        });

        return {
            series: convertYToNumber([...newSeries, ...planSeries]),
            yaxis: newYaxis,
            checkedMetrics: localCheckedMetrics,
        };
    }, [chartType, isDataValid, data, _metricsChecked, allGroupsChecked, percentages, details]);

    // Единый эффект для обновления series в Redux
    useEffect(() => {
        if (!isDataValid) {
            setLabels([]);
            setSeries([]);
            return;
        }

        switch (chartType) {
            case 'pie':
                if (pieSeries) setSeries(pieSeries as any);
                break;
            case 'bar':
                if (barSeries) setSeries(barSeries);
                break;
            case 'tunnel':
                if (tunnelSeries) setSeries(tunnelSeries);
                break;
            case 'speedometer':
                if (speedometerSeries) setSeries(speedometerSeries as any);
                break;
            case 'treemap':
                if (treemapSeries) setSeries(treemapSeries as any);
                break;
            case 'line':
            case 'area':
                if (timeSeriesData) {
                    setSeries(timeSeriesData.series);
                }
                break;
            default:
                break;
        }
    }, [chartType, isDataValid, pieSeries, barSeries, tunnelSeries, speedometerSeries, treemapSeries, timeSeriesData]);

    // ==================== РЕНДЕР ГРАФИКОВ ====================

    const NoDataMessage = useCallback(({ message }: { message: string }) => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <span style={{ fontSize: 20, color: '#999' }}>{message}</span>
        </div>
    ), []);

    const renderChartContent = useMemo(() => {
        const { updatedSeries, sortedSeries, isNullableData } = preparedSeriesData;
        // Используем effectiveChartHeight для адаптации под размер блока
        const chartH = effectiveChartHeight;

        const chartConfigs: Record<string, () => React.ReactElement> = {
            line: () => {
                const option = getLineChartOption(series, CHART_COLORS, isCompare, true, true, xAxisLabel, isAxesVisible, metricDisplaySettings, chartH, defaultTrendType || 'linear', isCompare);
                if (!option) return <div style={{ height: chartH }} />;
                return <EChart option={option} style={{ height: chartH }} lazyUpdate={true} notMerge />;
            },

            area: () => {
                const option = getAreaChartOption(series, CHART_COLORS, true, true, xAxisLabel, isAxesVisible, metricDisplaySettings, chartH, defaultTrendType || 'linear', isCompare);
                if (!option) return <div style={{ height: chartH }} />;
                return <EChart option={option} style={{ height: chartH }} lazyUpdate={true} notMerge />;
            },

            pie: () => {
                const option = getPieChartOption(updatedSeries, CHART_COLORS, false, chartH);
                if (!option || isNullableData) return <div style={{ height: chartH }} />;
                return <EChart option={option} style={{ height: chartH }} lazyUpdate={true} notMerge />;
            },

            bar: () => {
                const option = getBarChartOption(sortedSeries, CHART_COLORS);
                if (!option) return <div style={{ height: chartH }} />;
                return <EChart option={option} style={{ height: chartH }} lazyUpdate={true} notMerge />;
            },

            tunnel: () => {
                const option = getFunnelChartOption(updatedSeries, CHART_COLORS);
                if (!option || isNullableData) return <div style={{ height: chartH }} />;
                return <EChart option={option} style={{ height: chartH }} lazyUpdate={true} notMerge />;
            },

            speedometer: () => {
                const selectedMetric = _metricsChecked[0];
                if (!selectedMetric) return <NoDataMessage message="Выберите метрику и укажите план" />;
                if (!selectedMetric.plan) return <NoDataMessage message="Вы выбрали метрику, у которой не указан план" />;

                const metricValue = updatedSeries.reduce((acc: number, item: any) => acc + (item.value || 0), 0);
                const planValue = Number(modalState2.plan) || selectedMetric.plan || 100;
                const option = getGaugeChartOption(metricValue, planValue, CHART_COLORS);

                if (!option) return <NoDataMessage message="Выберите метрику и укажите план" />;
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <EChart option={option} style={{ height: chartH, width: Math.min(420, chartH) }} lazyUpdate={true} notMerge />
                    </div>
                );
            },

            treemap: () => {
                const option = getTreemapChartOption(series[0], CHART_COLORS);
                if (!option) return <NoDataMessage message="Выберите одну метрику и несколько группировок" />;
                return <EChart option={option} style={{ height: chartH }} lazyUpdate={true} notMerge />;
            },

            bubble: () => (
                <BubbleChartType
                    axisMinMax={offsetsBubbleChart}
                    tooltip={(title: string, items: any[]) => {
                        try {
                            const groupName = items[2]?.value;
                            return `
                                <div class="arrow_box">
                                    <b>${groupName}</b>
                                    <hr />
                                    <span>${_metricsChecked[0]?.label}: ${items[0]?.value}</span>
                                    <span>${_metricsChecked[1]?.label}: ${items[1]?.value}</span>
                                    <span>${_metricsChecked[2]?.label}: ${items[3]?.value}</span>
                                </div>
                            `;
                        } catch {
                            return '<div class="arrow_box"><span>Нет данных</span></div>';
                        }
                    }}
                    data={seriesForBubbleChart}
                    _metricsChecked={_metricsChecked}
                    lazyLoad={false}
                />
            ),
        };

        const renderChart = chartConfigs[chartType];
        if (!renderChart) return <NoDataMessage message="Неизвестный тип графика" />;

        return <div>{renderChart()}</div>;
    }, [
        tableData,
        groupsChecked,
        totalChecked,
        preparedSeriesData,
        series,
        xAxisLabel,
        isAxesVisible,
        metricDisplaySettings,
        defaultTrendType,
        chartType,
        _metricsChecked,
        modalState2,
        offsetsBubbleChart,
        seriesForBubbleChart,
        NoDataMessage,
        effectiveChartHeight,
        isCompare,
    ]);

    // ==================== ФИНАЛЬНЫЙ РЕНДЕР ====================

    // Проверяем, выбраны ли метрика и группировка для показа сообщения
    const hasMetricsSelected = _metricsChecked?.length > 0;
    const hasGroupsSelected = allGroupsChecked?.length > 0;
    const showEmptyMessage = !hasMetricsSelected || !hasGroupsSelected;

    const getEmptyMessage = () => {
        if (!hasMetricsSelected && !hasGroupsSelected) {
            return 'Выберите метрику и группировку для отображения графика';
        } else if (!hasMetricsSelected) {
            return 'Выберите метрику для отображения графика';
        } else {
            return 'Выберите группировку для отображения графика';
        }
    };
    
    // Определяем, показывать ли кнопки типа графика
    const showChartTypeButtons = !hideChartTypeButtons && displayMode === 'full';
    
    // Определяем высоту графика с учётом режима отображения
    const chartHeightValue = effectiveChartHeight;

    // Подпись периодов сравнения: без неё непонятно, какая линия к какому периоду относится
    const comparePeriodsLegend = useMemo(() => {
        // В блоках дашборда и превью места мало — подпись только на странице отчёта
        if (isCompactMode || !isCompare || !['line', 'area'].includes(chartType)) return null;

        const format = 'DD.MM.YYYY';
        const { comparePeriod1, comparePeriod2 } = comparePeriod;
        // Линии обоих периодов сплошные, второй период — светлый оттенок цвета метрики
        const items = [
            { label: 'Период 1', period: comparePeriod1, color: CHART_COLORS[0] },
            { label: 'Период 2', period: comparePeriod2, color: lightenColor(CHART_COLORS[0]) },
        ];

        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 20, padding: '4px 0 8px' }}>
                {items.map(({ label, period: p, color }) => (
                    <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#595959' }}>
                        <span
                            style={{
                                width: 28,
                                borderTop: `2.5px solid ${color}`,
                                display: 'inline-block',
                            }}
                        />
                        {label}: {moment(p?.start).format(format)} — {moment(p?.end).format(format)}
                    </span>
                ))}
            </div>
        );
    }, [isCompare, isCompactMode, chartType, comparePeriod]);

    return (
        <>
            {showChartTypeButtons && <ButtonsType />}
            {shouldShowChart && (
                <>
                    {comparePeriodsLegend}
                    <div
                        className="universal-chart"
                        id="universal_chart" 
                        style={{ 
                            minHeight: chartHeightValue,
                            position: 'relative',
                        }}
                    >
                        {showEmptyMessage ? (
                            <div style={{ 
                                width: '100%', 
                                height: chartHeightValue, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center' 
                            }}>
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={getEmptyMessage()}
                                />
                            </div>
                        ) : (
                            renderChartContent
                        )}
                        
                        {/* Кнопка открытия полного отчёта для compact/preview режимов */}
                        {isCompactMode && onOpenFullReport && (
                            <div 
                                style={{ 
                                    position: 'absolute', 
                                    top: 8, 
                                    right: 8, 
                                    zIndex: 10,
                                }}
                            >
                                <button
                                    onClick={onOpenFullReport}
                                    style={{
                                        padding: '4px 8px',
                                        fontSize: 12,
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 4,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                    }}
                                    title="Открыть полный отчёт"
                                >
                                    ↗
                                </button>
                            </div>
                        )}
                    </div>
                    {!hideTitle && titles.map((portalContent: any) => (
                        <TitleChart
                            key={portalContent.content}
                            left={portalContent.left as number}
                            top={portalContent.top}
                            content={portalContent.content}
                            color={getColorTitle(series, portalContent.content)}
                        />
                    ))}
                </>
            )}
        </>
    );
};

export default memo(ReportChart);
